import React, { useEffect, useRef } from 'react';
import {
  SubmitButton, Form, FormItem, Formik, FormikHelpers,
  Link, useNavigate, message, Row, Col, Typography, UserOutlined,
  InputTextField, InputPasswordField, LockOutlined, LeftCircleOutlined, useAppDispatch,
  useAppSelector, useTranslation,
} from '@lib/index';
import IntercomWatcher from '@app/intercomWatcher';
import ReCAPTCHA from 'react-google-recaptcha';
import { ENV } from '@config/envConfig';
import { clearState, loginUser } from '../../../_features/account/AccountSlice';
import { LoginFormProps, EmbeddedLoginProps } from '../types';
import { loginFormValidation } from '../schema/AuthFormValidation';
import { loginInitialValues } from '../schema/AuthDefaultValue';
import SelectProfile from '../SelectProfile';
import ChangePassword from './ChangePassword';

const LoginForm = ({ embedded = false }: EmbeddedLoginProps) => {
  const { Title } = Typography;
  const dispatch = useAppDispatch();
  const environment = ENV.VITE_NODE_ENV || import.meta.env.VITE_NODE_ENV;
  const { currentUser, isError } = useAppSelector((state) => state.account);
  const { roleId, token, passwordChange } = currentUser;
  const isAuthenticated = token !== '' && roleId !== null && passwordChange === false;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const reCaptchaSiteKey = (
    ENV.VITE_RECAPTCHA_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SITE_KEY
  ) as string;

  const onSubmit = (
    values: LoginFormProps,
    { setSubmitting }: FormikHelpers<LoginFormProps>,
  ) => {
    try {
      const { email, password } = values;
      const captchaToken = reCaptchaRef.current?.getValue();
      if (!captchaToken && environment === 'production') {
        message.warning(t('login.reCaptcha'), 5);
        setSubmitting(false);
        return;
      }

      dispatch(loginUser({ email, password, recaptcha: captchaToken || '' }));
      reCaptchaRef.current?.reset();
    } catch (e) {
      message.error(t('login.loginError'), 2);
      dispatch(clearState());
    }
    setSubmitting(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(clearState());
      const home = '/dashboard';
      navigate(home);
    }
    if (isError) {
      message.warning(
        t('login.loginError'),
        5,
      );
      dispatch(clearState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isAuthenticated, dispatch, navigate]);

  const formContent = (
    <Formik
      enableReinitialize
      initialValues={loginInitialValues}
      onSubmit={onSubmit}
      validationSchema={loginFormValidation}
    >
      <div className={embedded ? 'h-full bg-white' : 'p-6 sm:p-12 mt-4 h-full w-full bg-white'}>
        <Title level={4} className={`font-bold ${embedded ? 'text-center text-[#252B42] mb-1 mt-0 text-lg' : 'text-center text-lg'}`}>
          {t('login.login')}
        </Title>
        <Form name="normal_login" className="login-form">
          <div className="mb-0">
            <InputTextField
              name="email"
              prefix={<UserOutlined className="site-form-item-icon text-[#828282]" />}
              placeholder={t('login.email')}
              size="large"
              data-cy="login-form-email"
            />
          </div>
          <div className="mt-1 mb-0">
            <InputPasswordField
              name="password"
              prefix={<LockOutlined className="site-form-item-icon text-[#828282]" />}
              placeholder={t('login.password')}
              size="large"
              data-cy="login-form-password"
            />
          </div>
          <FormItem name="wrapper" className="text-right mb-1">
            <Link className="login-form-forgot text-[#3E82DA] font-medium text-[13px]" to="/reset-password">
              {t('login.forgotPassword')}
            </Link>
          </FormItem>
          <div className="flex justify-center mb-4">
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={reCaptchaSiteKey}
            />
          </div>
          <FormItem name="button" className="mb-0">
            <SubmitButton
              block
              size="large"
              data-cy="login-form-submit"
              className="bg-[#2B63E1] hover:bg-[#154fc1] border-0 h-10 font-bold text-base rounded-lg flex items-center justify-center transition-colors text-white disabled:text-white disabled:opacity-90"
            >
              {t('login.login')}
            </SubmitButton>
          </FormItem>
        </Form>
      </div>
    </Formik>
  );

  if (embedded) {
    return (
      <div className="landing-fonts">
        <IntercomWatcher />
        <div className="p-5 w-full bg-white rounded-2xl shadow-2xl overflow-hidden max-w-[370px] m-auto" data-cy="login-form">
          {formContent}
        </div>
        <ChangePassword requestPasswordChange={passwordChange} />
        <SelectProfile />
      </div>
    );
  }

  return (
    <div className="bg-login-bg bg-no-repeat bg-cover h-screen overflow-auto bg-center py-24 2xl:p-24 shadow-inner" data-cy="login-form">
      <IntercomWatcher />
      <Row align="middle" justify="center" className="mt-0">
        <Col xs={22} sm={18} md={12} lg={12} xl={6} className="mt-0">
          <Link to="/" className="text-white text-2xl">
            <LeftCircleOutlined className="text-white text-2xl pr-8" />
            {t('login.backToSite')}
          </Link>
          {formContent}
          <div className="bg-logo-bg bg-no-repeat bg-center h-56 bg-auto" />
          <ChangePassword requestPasswordChange={passwordChange} />
          <SelectProfile />
        </Col>
      </Row>
    </div>
  );
};
LoginForm.defaultProps = {
  embedded: false,
};

export default LoginForm;
